import React, { useState, useEffect, useCallback, useContext } from "react";
import { useParams } from "react-router-dom";
import { Box, Button, Typography, Divider } from "@mui/material";
import { getFileSize } from "../../../utils/file.js";
import { getQualificationHistoryApi, exportQualificationApi, uploadNewQualificationApi, exportQualificationTemplateApi } from "../../../api/safety";
import { NotificationContext } from "../../../component/popup/NotificationProvider";
import dayjs from "dayjs";
import Popup from "../../../component/popup/Popup.js";
import Table from "../../../component/table/MainTable.js";
import FileList from "../../../component/FileList.js";
import UploadNewFileButton from "../../../component/button/UploadNewFileButton.js";
import DownloadTemplateButton from "../../../component/button/DownloadTemplateButton.js";
import ExportDataButton from "../../../component/button/ExportDataButton.js";
import FullScreenLoading from "../../../component/FullScreenLoading.js";

const ManageQualificationPopup = ({ open, onClose, onUploadSuccess, user }) => {
  const { projectId } = useParams();
  const { showSuccess, showError } = useContext(NotificationContext);
  const [fileHistory, setFileHistory] = useState(null);
  const [latestFile, setLatestFile] = useState([]);
  const [isUploadButtonDisabled, setIsUploadButtonDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const modelHistoryHead = [
    { id: "filename", label: "Archive File Name", align: "left" },
    { id: "fileSize", label: "Size", align: "left" },
    {
      id: "uploadDate",
      label: "Upload Date",
      align: "left",
      type: "dateWithTime",
    },
    { id: "uploadBy", label: "Upload By", align: "left" },
    { id: "icon", label: "", align: "center" },
  ];

  const readonly = !user?.editRight;

  const getTableData = useCallback(async () => {
    try {
      const result = await getQualificationHistoryApi(projectId);
      const data = result?.data;

      if (!data || !Array.isArray(data)) {
        showError("Invalid or empty table data received.");
        return;
      }

      const formattedData = data.map((item) => ({
        ...item,
        uploadDate: dayjs(item.uploadDate).format("YYYY-MM-DD HH:mm"),
        fileSize: item.fileSize ? getFileSize(item.fileSize) : 0,
      }));

      const activeItem = data.find((item) => item.status === "active");
      if (activeItem) {
        setLatestFile([activeItem]);
      }

      setFileHistory(formattedData);
    } catch {
      showError("Received table data occurred error.");
    }
  }, []);

  const uploadNewVersion = async (e) => {
    const files = e.target.files;
    if (files.length > 0) {
      setIsLoading(true);
      const formData = new FormData();
      formData.append("file", files[0]);
      try {
        const result = await uploadNewQualificationApi(projectId, formData);

        if (result) {
          showSuccess("The Test and Training record has been successfully updated.");
          getTableData();
          onUploadSuccess();
        } else {
          showError("Upload failed.");
        }
      } catch (error) {
        if (error.response.data.detail) {
          showError(error.response.data.detail);
        } else {
          showError("Upload failed.");
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  const downloadFile = (item) => {
    setIsLoading(true);
    const link = document.createElement("a");
    link.href = item?.fileURL;
    link.download = item?.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => {
      showSuccess("The file has been successfully downloaded.");
      setIsLoading(false);
    }, 3000);
  };

  const downloadTemplate = async () => {
    setIsLoading(true);
    try {
      const result = await exportQualificationTemplateApi();
      const templateUrl = result?.data;
      if (templateUrl) {
        const link = document.createElement("a");
        link.href = templateUrl;
        link.download = "template";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        showSuccess("The file has been successfully downloaded.");
      } else {
        showError("Download failed.");
      }
    } catch {
      showError("Download failed.");
    } finally {
      setIsLoading(false);
    }
  };

  const exportCurrentData = async () => {
    setIsLoading(true);
    try {
      const result = await exportQualificationApi(projectId);
      if (result) {
        const data = result?.data;
        const link = document.createElement("a");
        link.href = data;
        link.download = data?.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        showSuccess("The file has been successfully downloaded.");
      } else {
        showError("Export failed.");
      }
    } catch {
      showError("Export failed.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getTableData();
  }, [getTableData]);

  useEffect(() => {
    if (fileHistory && fileHistory.length > 0) {
      setIsUploadButtonDisabled(true || readonly);
    } else {
      setIsUploadButtonDisabled(false || readonly);
    }
  }, [fileHistory, readonly]);

  return (
    <>
      <Popup
        open={open}
        onClose={onClose}
        modalStyle={{
          "& .MuiBox-root.css-ft9d2q": {
            borderRadius: "10px 10px 0 0",
          },
        }}
        popupStyle={{
          minWidth: 1000,
          maxWidth: 1000,
        }}
        title="Manage Test & Training"
      >
        <Box maxHeight={800} overflow="auto">
          <Box
            sx={{
              mx: 4,
              my: 2,
              p: 2,
              background: "#F5F5F5",
              borderRadius: "10px",
            }}
          >
            <Typography variant="h6" component="span">
              Test & Training
            </Typography>
            <Typography component="span">&nbsp;&nbsp;* Files shall be in Excel format</Typography>
            <Divider variant="fullWidth" />
            <Box sx={{ display: "flex", mb: 3, gap: 3 }}>
              <Typography component="span" sx={{ pt: 1.5 }}>
                current:
              </Typography>
              <FileList
                files={latestFile}
                onUpload={(event) => uploadNewVersion(event)}
                showRemoveButton={false}
                onDownload={downloadFile}
                accept=".xlsx,.xls,.xlsm,.xml"
                disabled={readonly}
              />
            </Box>
            <Table
              columns={modelHistoryHead}
              rows={fileHistory}
              iconColumnName="icon"
              headerStyles={{
                default: { backgroundColor: "#D0D5DE" },
              }}
              iconImage={"assets/icons/clonedownload.svg"}
              columnWidths={{
                icon: "100px",
                fileSize: "130px",
                uploadDate: "160px",
                uploadBy: "130px",
              }}
              sx={{ maxHeight: 200 }}
              onHistoryUploadSuccess={() => showSuccess("The file has been successfully downloaded.")}
            />
            <Box display="flex" alignItems="center" justifyContent="center" gap={2} mt={2}>
              <UploadNewFileButton
                accept=".xlsx,.xls,.xlsm,.xml"
                disabled={isUploadButtonDisabled}
                onChange={(event) => {
                  uploadNewVersion(event);
                }}
              ></UploadNewFileButton>
              <DownloadTemplateButton onClick={downloadTemplate} disabled={readonly}></DownloadTemplateButton>
              <ExportDataButton onClick={exportCurrentData}></ExportDataButton>
            </Box>
          </Box>
        </Box>
        <Box textAlign="center">
          <Button variant="outlined" color="inherit" onClick={onClose} sx={{ borderRadius: "10px", lineHeight: "normal", mb: 2 }}>
            Back
          </Button>
        </Box>
      </Popup>
      <FullScreenLoading isLoading={isLoading} />
    </>
  );
};

export default ManageQualificationPopup;
